import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Building2, Maximize2, Minimize2, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { RenataPanel } from "./RenataPanel";
import { useTenantContextSafe } from "@/lib/tenant-context";
import { Link, useLocation } from "@tanstack/react-router";
import { useSession } from "@/hooks/use-session";
import { clientRequestHeaders } from "@/lib/sm-api";
import {
  getRenataWorkspaceFocus,
  subscribeRenataWorkspaceFocus,
  type RenataWorkspaceFocus,
} from "@/lib/renata/workspace-focus";
import { RenataAvatar } from "./RenataAvatar";

const ADMIN_TENANT_KEY = "sm.renata.adminTenantId";
const CONV_KEY_PREFIX = "sm.renata.conversationId:";

type TenantOption = { id: string; name: string };

function convStorageKey(tenantId: string) {
  return `${CONV_KEY_PREFIX}${tenantId}`;
}

function useSheetOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const check = () => {
      const sheet = document.querySelector('[data-state="open"][role="dialog"].fixed');
      // Radix Sheet content is role=dialog; overlay is separate
      setOpen(Boolean(sheet));
    };
    check();
    const mo = new MutationObserver(check);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-state", "class"] });
    return () => mo.disconnect();
  }, []);
  return open;
}

/**
 * FAB Renata (portal a body, z alto):
 * - No queda debajo del Sheet de edición de productos (evita click en Guardar).
 * - Con drawer abierto se desplaza a la izquierda del panel.
 * - En mobile se eleva por encima del bottom nav para no tapar Ajustes.
 */
export function RenataFloatingButton() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [adminTenantId, setAdminTenantId] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantOption[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [focus, setFocus] = useState<RenataWorkspaceFocus | null>(() => getRenataWorkspaceFocus());
  const [mounted, setMounted] = useState(false);
  const tenantCtx = useTenantContextSafe();
  const location = useLocation();
  const { user } = useSession();
  const sheetOpen = useSheetOpen();

  useEffect(() => setMounted(true), []);
  useEffect(() => subscribeRenataWorkspaceFocus(() => setFocus(getRenataWorkspaceFocus())), []);

  // Listen for launcher-triggered open events (RenataLauncher → sm-renata-open)
  useEffect(() => {
    function handleLauncherOpen() {
      setOpen(true);
    }
    window.addEventListener("sm-renata-open", handleLauncherOpen);
    return () => window.removeEventListener("sm-renata-open", handleLauncherOpen);
  }, []);

  const onApp = location.pathname.startsWith("/app");
  const onAdmin = location.pathname.startsWith("/admin");
  const isSuperAdmin = user?.role === "super_admin";

  const appEnabled = onApp && tenantCtx?.productFlags?.ai_assistant === true && !!tenantCtx.tenantId;
  const adminEnabled = onAdmin && isSuperAdmin;
  const visible = appEnabled || adminEnabled;

  const tenantId = appEnabled ? tenantCtx!.tenantId : adminTenantId;

  useEffect(() => {
    if (!tenantId || typeof window === "undefined") {
      setConversationId(null);
      return;
    }
    setConversationId(localStorage.getItem(convStorageKey(tenantId)));
  }, [tenantId]);

  useEffect(() => {
    if (!adminEnabled) return;
    const stored =
      typeof window !== "undefined" ? localStorage.getItem(ADMIN_TENANT_KEY) : null;
    if (stored) setAdminTenantId(stored);

    void fetch("/api/admin/tenants", {
      credentials: "include",
      headers: clientRequestHeaders(),
    })
      .then(async (r) => {
        if (!r.ok) return;
        const data = (await r.json()) as
          | Array<{ id: string; name?: string }>
          | {
              tenants?: Array<{ id: string; name?: string }>;
              items?: Array<{ id: string; name?: string }>;
            };
        const raw = Array.isArray(data) ? data : (data.tenants ?? data.items ?? []);
        const list = raw
          .filter((t) => t.id)
          .map((t) => ({ id: t.id, name: t.name?.trim() || t.id }));
        setTenants(list);
        if (!stored && list[0]) {
          setAdminTenantId(list[0].id);
          localStorage.setItem(ADMIN_TENANT_KEY, list[0].id);
        }
      })
      .catch(() => {});
  }, [adminEnabled]);

  function selectAdminTenant(id: string) {
    setAdminTenantId(id);
    localStorage.setItem(ADMIN_TENANT_KEY, id);
  }

  function handleConversationChange(id: string | null) {
    setConversationId(id);
    if (!tenantId || typeof window === "undefined") return;
    if (id) localStorage.setItem(convStorageKey(tenantId), id);
    else localStorage.removeItem(convStorageKey(tenantId));
  }

  const onExperience = location.pathname === "/app/experience";
  const onOnboarding = location.pathname === "/app/onboarding";
  /** Bottom nav is `lg:hidden` on /app (except experience/onboarding) y `md:hidden` en /admin (SCRUM-258). */
  const liftForBottomNav = (onApp && !onExperience && !onOnboarding) || onAdmin;

  if (!visible || !mounted) return null;

  // Encima del footer mobile; desktop permanece bottom-4.
  const mobileAboveNav =
    "bottom-[max(5.75rem,calc(5rem+env(safe-area-inset-bottom)))] lg:bottom-4";
  const mobilePanelAboveNav =
    "bottom-[max(9.25rem,calc(8.5rem+env(safe-area-inset-bottom)))] lg:bottom-20";

  // Con drawer abierto: FAB a la izquierda del sheet (no encima de Guardar)
  const fabPos = cn(
    sheetOpen && !expanded ? "left-4 right-auto" : "right-4",
    liftForBottomNav ? mobileAboveNav : "bottom-4",
  );

  const panelPos = expanded
    ? "inset-3 sm:inset-6"
    : cn(
        sheetOpen ? "left-4 right-auto" : "right-4",
        "h-[min(640px,calc(100dvh-6rem))] w-[min(420px,calc(100vw-2rem))]",
        liftForBottomNav ? mobilePanelAboveNav : "bottom-20",
      );

  const focusHint =
    focus?.entity && focus.entityId
      ? `${focus.entity}: ${focus.entityName || focus.entityId}`
      : null;

  const ui = (
    <div
      data-renata-root=""
      className="pointer-events-none fixed inset-0 z-[10000]"
    >
      {tenantId && (
        <div
          data-renata-root=""
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className={cn(
            "pointer-events-auto fixed flex flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl",
            panelPos,
            !open && "pointer-events-none invisible opacity-0",
            open && "opacity-100",
          )}
          aria-hidden={!open}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-violet-200/80 bg-violet-100 px-3 py-2.5 text-violet-950 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-50">
            <div className="flex min-w-0 flex-1 items-center gap-2">
              <RenataAvatar size="xs" />
              <div className="min-w-0">
                <span className="text-sm font-semibold text-violet-950 dark:text-violet-50">Renata</span>
                {focusHint ? (
                  <p className="truncate text-[10px] text-violet-800 dark:text-violet-200">
                    Editando · {focusHint}
                  </p>
                ) : adminEnabled ? (
                  <p className="truncate text-[10px] text-violet-800 dark:text-violet-200">
                    Panel de administración · negocio
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-0.5">
              {onApp && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-7 w-7 text-violet-900 hover:bg-violet-200/90 hover:text-violet-950 dark:text-violet-100 dark:hover:bg-violet-900 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-violet-500"
                  asChild
                  title="Abrir pantalla completa"
                  aria-label="Abrir pantalla completa"
                >
                  <Link to="/app/renata/chat" search={{ c: conversationId ?? undefined } as never}>
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </Button>
              )}
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-violet-900 hover:bg-violet-200/90 hover:text-violet-950 dark:text-violet-100 dark:hover:bg-violet-900 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-violet-500"
                onClick={() => setExpanded((v) => !v)}
                title={expanded ? "Minimizar" : "Expandir"}
                aria-label={expanded ? "Minimizar chat" : "Expandir chat"}
              >
                {expanded ? <Minimize2 className="h-3.5 w-3.5" /> : <Maximize2 className="h-3.5 w-3.5" />}
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-7 w-7 text-violet-900 hover:bg-violet-200/90 hover:text-violet-950 dark:text-violet-100 dark:hover:bg-violet-900 dark:hover:text-white focus-visible:ring-2 focus-visible:ring-violet-500"
                onClick={() => {
                  setOpen(false);
                  setExpanded(false);
                }}
                title="Cerrar"
                aria-label="Cerrar chat"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {adminEnabled && (
            <div className="flex shrink-0 items-center gap-2 border-b bg-muted/30 px-3 py-2">
              <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <select
                className="h-8 w-full rounded-md border bg-background px-2 text-xs"
                value={tenantId}
                onChange={(e) => selectAdminTenant(e.target.value)}
                aria-label="Workspace para Renata"
              >
                {tenants.length === 0 && <option value={tenantId}>{tenantId}</option>}
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="min-h-0 flex-1 overflow-hidden" key={tenantId}>
            <RenataPanel
              tenantId={tenantId}
              currentRoute={location.pathname}
              compact
              initialConversationId={conversationId}
              onConversationChange={handleConversationChange}
              workspaceFocus={focus}
            />
          </div>
        </div>
      )}

      <button
        type="button"
        data-renata-root=""
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "pointer-events-auto fixed overflow-hidden",
          fabPos,
          "flex h-12 w-12 items-center justify-center rounded-full shadow-lg",
          open ? "bg-violet-600 text-white hover:bg-violet-700" : "bg-black p-0 hover:opacity-90",
          "transition-opacity",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400 focus-visible:ring-offset-2",
        )}
        aria-label={open ? "Cerrar Renata" : "Abrir Renata"}
        title={focusHint ? `Renata · ${focusHint}` : "Renata"}
      >
        {open ? <X className="h-5 w-5" /> : <RenataAvatar size="lg" alt="" className="h-12 w-12" />}
      </button>
    </div>
  );

  return createPortal(ui, document.body);
}
